import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const FALLBACK_PLANS = [
  { planCode: 'monthly', planName: 'Monthly Subscription', originalPrice: 1999, sellingPrice: 499, durationDays: 30 },
  { planCode: 'quarterly', planName: 'Quarterly Subscription', originalPrice: 5999, sellingPrice: 999, durationDays: 90 },
  { planCode: 'annual', planName: 'Annual Subscription', originalPrice: 23999, sellingPrice: 2499, durationDays: 365 },
];

const BENEFITS = {
  monthly: [
    'Appointments and visit flow',
    'Prescription Pad and dental chart',
    'Invoice, payments, EMI records',
    'Patient History and follow-ups',
    'Pricing Engine, inventory, reminders',
  ],
  quarterly: [
    'All monthly clinic workflow benefits',
    'Better value for continuous clinic management',
    'Appointments, prescriptions, invoices, EMI',
    'Patient History, Pricing Engine, inventory',
    'Auto reminders and operational continuity',
  ],
  annual: [
    'Best value full clinic operating system',
    'EMI reminders and patient history',
    'Pricing intelligence and inventory control',
    'Business reports, Supply Hub, Doctor’s Circle',
    'Dashboard command center for daily clinic work',
  ],
};
const INDIAN_MOBILE_REGEX = /^[6-9]\d{9}$/;

function normalizeIndianMobile(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 12 && digits.startsWith('91') ? digits.slice(2) : digits;
}

function formatRupees(value) {
  return `\u20b9${Number(value || 0).toLocaleString('en-IN')}`;
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function Subscribe() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get('plan') || 'monthly');
  const [contactNumber, setContactNumber] = useState('');
  const [paymentMode, setPaymentMode] = useState('full');
  const [discountCode, setDiscountCode] = useState(searchParams.get('code') || searchParams.get('ref') || '');
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [apiWarning, setApiWarning] = useState('');
  const [referralCodeStatus, setReferralCodeStatus] = useState(null);
  const referralSalesCode = (searchParams.get('rsoffer') || '').trim().toUpperCase();
  const [referralSalesOffer, setReferralSalesOffer] = useState(null);
  const [referralSalesError, setReferralSalesError] = useState('');
  const referralSalesNonceRef = useRef(typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : '123e4567-e89b-42d3-a456-426614174000');
  const quoteInFlightRef = useRef(false);
  const checkoutInFlightRef = useRef(false);

  const attribution = useMemo(() => ({
    code: searchParams.get('code') || '',
    ref: searchParams.get('ref') || '',
    salesman: searchParams.get('salesman') || '',
    utm_source: searchParams.get('utm_source') || '',
    utm_medium: searchParams.get('utm_medium') || '',
    utm_campaign: searchParams.get('utm_campaign') || '',
    utm_content: searchParams.get('utm_content') || '',
    utm_term: searchParams.get('utm_term') || '',
    landingPageUrl: window.location.href,
  }), [searchParams]);

  useEffect(() => {
    let active = true;
    api.get('/subscriptions/public/pricing')
      .then(({ data }) => {
        if (!active) return;
        setPlans((data.plans || []).map((plan) => ({
          ...plan,
          planName: `${plan.planName || plan.planCode} Subscription`,
        })));
      })
      .catch(() => {
        if (!active) return;
        setPlans(FALLBACK_PLANS);
        setApiWarning('Live pricing could not be loaded. Checkout still requires backend pricing before payment.');
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (!ref) return;
    let active = true;
    api.post('/referrals/validate', { code: ref })
      .then(({ data }) => { if (active) setReferralCodeStatus({ valid: true, code: data.code }); })
      .catch((err) => { if (active) setReferralCodeStatus({ valid: false, error: err?.response?.data?.error || 'Referral code is invalid.' }); });
    return () => { active = false; };
  }, [searchParams]);

  useEffect(() => {
    if (!referralSalesCode) return undefined;
    let active = true; setReferralSalesError('');
    api.post('/subscriptions/public/referral-sales/offers/validate', { offerCode: referralSalesCode })
      .then(({ data }) => { if (active) { setReferralSalesOffer(data); setSelectedPlan(data.planCode); setPaymentMode('full'); } })
      .catch((err) => { if (active) { setReferralSalesOffer(null); setReferralSalesError(err?.response?.data?.error || 'This referral offer is unavailable.'); } });
    return () => { active = false; };
  }, [referralSalesCode]);

  const displayedPlans = plans.length ? plans : FALLBACK_PLANS;
  const selected = displayedPlans.find((plan) => plan.planCode === selectedPlan) || displayedPlans[0];
  const normalizedContactNumber = normalizeIndianMobile(contactNumber);
  const contactNumberIsValid = INDIAN_MOBILE_REGEX.test(normalizedContactNumber);
  const planIsValid = Boolean(selected?.planCode);
  const canProceed = !loading
    && planIsValid
    && contactNumberIsValid
    && (paymentMode === 'full' || Boolean(quote));
  const referralPlanMismatch = referralSalesOffer && selectedPlan !== referralSalesOffer.planCode;

  const requestQuote = async ({ manageLoading = true } = {}) => {
    if (quoteInFlightRef.current) return null;
    quoteInFlightRef.current = true;
    setError('');
    setQuote(null);
    if (!contactNumberIsValid) {
      setError('Enter a valid 10-digit Indian mobile number before checkout.');
      quoteInFlightRef.current = false;
      return null;
    }
    if (paymentMode === 'discount' && !discountCode.trim()) {
      setError('Enter discount, sales, or referral code.');
      quoteInFlightRef.current = false;
      return null;
    }
    if (manageLoading) setLoading(true);
    try {
      const { data } = await api.post('/subscriptions/checkout/quote', {
        planCode: selectedPlan,
        contactNumber: contactNumber.trim(),
        paymentMode,
        discountCode: paymentMode === 'discount' ? discountCode.trim() : null,
        attribution,
      });
      setQuote(data);
      return data;
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to calculate final amount.');
      return null;
    } finally {
      quoteInFlightRef.current = false;
      if (manageLoading) setLoading(false);
    }
  };

  const proceedToPayment = async () => {
    if (checkoutInFlightRef.current) return;
    setError('');
    if (!contactNumberIsValid) {
      setError('Enter a valid 10-digit Indian mobile number before checkout.');
      return;
    }
    if (!planIsValid) {
      setError('Choose a valid subscription plan before checkout.');
      return;
    }
    checkoutInFlightRef.current = true;
    setLoading(true);
    let checkoutOpened = false;
    try {
      if (referralPlanMismatch) {
        setError(`This referral offer is valid for the ${referralSalesOffer.planCode[0].toUpperCase() + referralSalesOffer.planCode.slice(1)} plan. Please select that plan or use another offer.`);
        return;
      }
      const lockedQuote = referralSalesOffer || quote || await requestQuote({ manageLoading: false });
      if (!lockedQuote) return;
      const { data: order } = await api.post('/subscriptions/checkout/create-order', {
        planCode: selectedPlan,
        contactNumber: contactNumber.trim(),
        paymentMode,
        discountCode: paymentMode === 'discount' ? discountCode.trim() : null,
        attribution,
        ...(referralSalesOffer ? { referralSalesOfferCode: referralSalesCode, referralSalesRequestNonce: referralSalesNonceRef.current } : {}),
      });
      const ready = await loadRazorpayScript();
      if (!ready || !window.Razorpay) {
        throw new Error('Razorpay is not available. Please check internet access and try again.');
      }
      const razorpay = new window.Razorpay({
        key: order.razorpayKeyId,
        amount: order.amount,
        currency: order.currency,
        name: 'Dektal 360 Pro',
        description: `${order.quote?.planName || selected.planName} checkout`,
        order_id: order.razorpayOrderId || order.orderId,
        prefill: { contact: contactNumber.trim() },
        handler: async (response) => {
          try {
            const { data } = await api.post('/subscriptions/checkout/verify-payment', {
              checkoutSessionId: order.checkoutSessionId,
              checkoutSession: order.checkoutSessionId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              referralCheckoutToken: order.referralCheckoutToken,
            });
            sessionStorage.setItem('paidSubscriptionCheckout', JSON.stringify(data.payment || {}));
            checkoutInFlightRef.current = false;
            setLoading(false);
            navigate(data.redirectTarget || `/register-after-payment?token=${encodeURIComponent(data.paidOnboardingToken || '')}`, { replace: true });
          } catch (verifyError) {
            checkoutInFlightRef.current = false;
            setLoading(false);
            navigate('/payment-failed', {
              replace: true,
              state: { error: verifyError?.response?.data?.error || verifyError.message },
            });
          }
        },
        modal: {
          ondismiss: () => {
            checkoutInFlightRef.current = false;
            setLoading(false);
          },
        },
      });
      razorpay.on('payment.failed', (response) => {
        checkoutInFlightRef.current = false;
        setLoading(false);
        navigate('/payment-failed', {
          replace: true,
          state: { error: response?.error?.description || 'Razorpay payment failed.' },
        });
      });
      razorpay.open();
      checkoutOpened = true;
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Payment could not be started.');
    } finally {
      if (!checkoutOpened) {
        checkoutInFlightRef.current = false;
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-blue-600">Dektal 360 Pro</div>
            <h1 className="mt-2 text-3xl font-bold text-slate-950">Choose your clinic subscription</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Pay securely first, then complete clinic registration. Existing doctors can login without paying again.
            </p>
          </div>
          <Link to="/login" className="rounded-2xl border border-blue-200 bg-white px-4 py-3 text-sm font-semibold text-blue-700">
            Login to Software
          </Link>
        </div>

        {apiWarning ? <div className="mb-5 rounded-2xl bg-amber-50 p-4 text-sm text-amber-800">{apiWarning}</div> : null}
        {referralCodeStatus ? <div className={`mb-5 rounded-2xl p-4 text-sm ${referralCodeStatus.valid ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}`}>{referralCodeStatus.valid ? `Referral code ${referralCodeStatus.code} is valid and will be preserved through registration. Qualification happens only after manual review.` : referralCodeStatus.error}</div> : null}
        {referralSalesError ? <div className="mb-5 rounded-2xl bg-rose-50 p-4 text-sm text-rose-800">{referralSalesError}<button type="button" onClick={() => { setReferralSalesError(''); window.history.replaceState({}, '', '/subscribe'); }} className="ml-2 font-bold underline">Continue without offer</button></div> : null}
        {referralSalesOffer ? <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900"><p className="font-bold">Special Referral Sales offer</p><div className="mt-2 grid gap-2 sm:grid-cols-4"><span>Plan: <b className="capitalize">{referralSalesOffer.planCode}</b></span><span>Regular offer: <b>{formatRupees(referralSalesOffer.sellingPricePaise / 100)}</b></span><span>Friend discount: <b>{formatRupees(referralSalesOffer.friendDiscountPaise / 100)}</b></span><span>Special price: <b>{formatRupees(referralSalesOffer.friendPaysPaise / 100)}</b></span></div><p className="mt-2 text-xs">Expires {new Date(referralSalesOffer.expiresAt).toLocaleString('en-IN')}</p></div> : null}

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
          <div className="grid gap-5 md:grid-cols-3">
            {displayedPlans.map((plan) => (
              <button
                type="button"
                key={plan.planCode}
                onClick={() => {
                  setSelectedPlan(plan.planCode);
                  setQuote(null);
                  setError('');
                  if (referralSalesOffer && plan.planCode !== referralSalesOffer.planCode) setError(`This referral offer is valid for the ${referralSalesOffer.planCode[0].toUpperCase() + referralSalesOffer.planCode.slice(1)} plan. Please select that plan or use another offer.`);
                }}
                className={`flex flex-col rounded-3xl border bg-white p-6 text-left shadow-sm transition md:min-h-[520px] ${selectedPlan === plan.planCode ? 'border-blue-500 ring-2 ring-blue-100' : 'border-blue-100 hover:border-blue-300'}`}
              >
                <div className="text-sm font-semibold uppercase tracking-wide text-blue-600">{plan.planCode}</div>
                <h2 className="mt-3 text-2xl font-bold text-slate-950">{plan.planName}</h2>
                <div className="mt-5 flex flex-wrap items-end gap-2">
                  <span className="mobile-safe-text text-3xl font-bold text-slate-950 sm:text-4xl">{formatRupees(plan.sellingPrice)}</span>
                  <span className="pb-1 text-sm text-slate-400 line-through">{formatRupees(plan.originalPrice)}</span>
                </div>
                <div className="mt-2 text-sm text-slate-500">{plan.durationDays} days access</div>
                <ul className="mt-6 space-y-3 text-sm text-slate-700">
                  {(BENEFITS[plan.planCode] || BENEFITS.monthly).map((benefit) => (
                    <li key={benefit} className="rounded-2xl bg-slate-50 px-3 py-2">{benefit}</li>
                  ))}
                </ul>
                <div className="mt-auto pt-6">
                  <div className="rounded-2xl bg-blue-600 px-4 py-3 text-center text-sm font-semibold text-white">
                    Subscribe
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="rounded-3xl bg-white p-6 shadow-xl">
            <h2 className="text-xl font-bold text-slate-950">Checkout</h2>
            <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50 p-4">
              <div className="text-sm text-blue-700">Selected plan</div>
              <div className="mt-1 text-lg font-bold text-slate-950">{selected?.planName}</div>
              <div className="mt-2 text-sm text-slate-600">{referralSalesOffer && !referralPlanMismatch ? `${formatRupees(referralSalesOffer.friendPaysPaise / 100)} special price` : `${formatRupees(selected?.sellingPrice)} launch price`}</div>
            </div>

            <div className="mt-5 space-y-4">
              <label className="block">
                <span className="text-sm font-semibold text-slate-700">Contact number</span>
                <input
                  value={contactNumber}
                  onChange={(event) => {
                    setContactNumber(event.target.value);
                    setQuote(null);
                    setError('');
                  }}
                  type="tel"
                  inputMode="numeric"
                  aria-label="Contact number"
                  aria-invalid={Boolean(contactNumber) && !contactNumberIsValid}
                  placeholder="+91 98765 43210"
                  className="mt-1 w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500"
                />
                <span className={`mt-2 block text-xs ${contactNumber && !contactNumberIsValid ? 'font-semibold text-rose-600' : 'text-slate-500'}`}>
                  Enter a valid 10-digit Indian mobile number.
                </span>
              </label>

              {!referralSalesOffer ? <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => {
                    setPaymentMode('full');
                    setQuote(null);
                    setError('');
                    setDiscountCode(searchParams.get('code') || searchParams.get('ref') || '');
                  }}
                  className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${paymentMode === 'full' ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-slate-200 text-slate-700'}`}
                >
                  Pay in full
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPaymentMode('discount');
                    setQuote(null);
                    setError('');
                  }}
                  className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${paymentMode === 'discount' ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-slate-200 text-slate-700'}`}
                >
                  Pay with discount code
                </button>
              </div> : null}

              {paymentMode === 'discount' ? (
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">Discount / sales / referral code</span>
                  <input
                    value={discountCode}
                    onChange={(event) => {
                      setDiscountCode(event.target.value);
                      setQuote(null);
                    }}
                    placeholder="Enter code"
                    className="mt-1 w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500"
                  />
                </label>
              ) : null}
            </div>

            {paymentMode === 'discount' ? (
              <button type="button" onClick={() => requestQuote()} disabled={loading} className="mt-5 w-full rounded-2xl border border-blue-200 bg-white px-4 py-3 text-sm font-semibold text-blue-700 disabled:opacity-60">
                {loading ? 'Calculating...' : 'Calculate final amount'}
              </button>
            ) : null}

            {quote ? (
              <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                <div className="flex justify-between"><span>Selling price</span><b>{formatRupees(quote.sellingPrice)}</b></div>
                <div className="mt-2 flex justify-between"><span>Discount</span><b>{formatRupees(quote.discountAmount)}</b></div>
                <div className="mt-3 flex justify-between border-t border-emerald-200 pt-3 text-lg"><span>Final amount</span><b>{formatRupees(quote.finalAmount)}</b></div>
                <p className="mt-3">{quote.message}</p>
              </div>
            ) : null}

            <button type="button" onClick={proceedToPayment} disabled={!canProceed || Boolean(referralSalesError) || Boolean(referralPlanMismatch)} className="mt-4 w-full rounded-2xl bg-blue-600 px-4 py-3 font-semibold text-white disabled:opacity-60">
              {loading ? 'Opening Razorpay...' : 'Proceed to Payment'}
            </button>

            {error ? <div className="mt-4 rounded-2xl bg-rose-50 p-3 text-sm text-rose-700">{error}</div> : null}
            <p className="mt-5 text-xs leading-5 text-slate-500">
              Razorpay amount is created and locked by backend. Clinic tenant is created only after verified payment and registration completion.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
